import React, { useEffect, useMemo, useState } from 'react';
import { MdSearch, MdCheckCircle } from 'react-icons/md';
import PageHeader from '../components/PageHeader';
import SkeletonRow from '../components/SkeletonRow';
import StatusToggle from '../../../Components/StatusToggle';
import { gatemanApi } from '../../../api/adminapi';
import '../Admin.css';
import './BookRoom.css';
import './GatemenApproval.css';

function GatemenApproval() {
  const [list, setList] = useState([]);
  const [filters, setFilters] = useState({ query: '', status: 'all' });
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [error, setError] = useState('');
  const [savingEmail, setSavingEmail] = useState('');
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivateData, setDeactivateData] = useState({ email: '', name: '', reason: '' });

  useEffect(() => {
    void loadApprovals();
  }, []);

  const loadApprovals = async () => {
    try {
      setIsTableLoading(true);
      setError('');
      const payload = await gatemanApi.getApprovalSummaries();
      const approvals = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
      setList(approvals);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to fetch gatemen approvals');
    } finally {
      setIsTableLoading(false);
    }
  };

  const handleStatusToggle = async (email, nextIsApproved) => {
    // If turning OFF approval, show confirmation modal with reason (like Users/Routes)
    if (!nextIsApproved) {
      const row = list.find(it => it.email === email);
      const name = row?.name || '';
      setDeactivateData({ email, name, reason: '' });
      setShowDeactivateModal(true);
      return;
    }

    // Approving can proceed directly
    try {
      setSavingEmail(email);
      setError('');
      await gatemanApi.updateApprovalStatus(email, true);
      setList(prev => prev.map(it => it.email === email ? { ...it, appovedByAdmin: true } : it));
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to update request');
    } finally {
      setSavingEmail('');
    }
  };

  const handleCloseDeactivateModal = () => {
    setShowDeactivateModal(false);
    setDeactivateData({ email: '', name: '', reason: '' });
    setError('');
  };

  const handleConfirmDeactivation = async () => {
    if (!deactivateData.reason.trim()) {
      setError('Please provide a reason for un-approving this request.');
      return;
    }
    try {
      setSavingEmail(deactivateData.email);
      setError('');
      await gatemanApi.updateApprovalStatus(deactivateData.email, false);
      setList(prev => prev.map(it => it.email === deactivateData.email ? { ...it, appovedByAdmin: false } : it));
      setShowDeactivateModal(false);
      setDeactivateData({ email: '', name: '', reason: '' });
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to update request');
    } finally {
      setSavingEmail('');
    }
  };

  const filtered = useMemo(() => {
    const q = (filters.query || '').toLowerCase().trim();
    return list.filter((g) => {
      // Status filter
      if (filters.status === 'approved' && !g.appovedByAdmin) return false;
      if (filters.status === 'pending' && g.appovedByAdmin) return false;

      const name = (g.name || '').toLowerCase();
      const email = (g.email || '').toLowerCase();
      const phone = (g.phoneNumber || '').toLowerCase();
      return !q || name.includes(q) || email.includes(q) || phone.includes(q);
    });
  }, [list, filters]);

  // Calculate counts for filter tabs
  const counts = useMemo(() => ({
    all: list.length,
    approved: list.filter(g => g.appovedByAdmin).length,
    pending: list.filter(g => !g.appovedByAdmin).length
  }), [list]);

  return (
    <div className="admin-view gatemen-approval-view">
      <PageHeader breadcrumbs={["Community", "Gatemen Approval"]} />

      {/* Compact Header with Filters */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <div className="filter-tabs">
            <button
              className={'filter-tab' + (filters.status === 'all' ? ' active' : '')}
              onClick={() => setFilters(prev => ({ ...prev, status: 'all' }))}
            >
              All ({counts.all})
            </button>
            <button
              className={'filter-tab' + (filters.status === 'approved' ? ' active' : '')}
              onClick={() => setFilters(prev => ({ ...prev, status: 'approved' }))}
            >
              Approved ({counts.approved})
            </button>
            <button
              className={'filter-tab' + (filters.status === 'pending' ? ' active' : '')}
              onClick={() => setFilters(prev => ({ ...prev, status: 'pending' }))}
            >
              Pending ({counts.pending})
            </button>
          </div>
        </div>
        <div className="header-filters-compact">
          <div className="input search-compact">
            <span className="icon"><MdSearch /></span>
            <input
              placeholder="Search by email, name, or phone..."
              value={filters.query}
              onChange={(e) => setFilters({ ...filters, query: e.target.value })}
            />
          </div>
        </div>
      </div>

      <div className="table card">
        <div className="table-head">
          <div className="cell">Email</div>
          <div className="cell">Status</div>
        </div>

        {isTableLoading && Array.from({ length: 6 }).map((_, idx) => (
          <SkeletonRow key={idx} />
        ))}

        {!isTableLoading && filtered && filtered.length > 0 && filtered.map((g, idx) => (
          <div className="table-row" key={g.email || idx}>
            <div className="cell email-cell">{g.email || ''}</div>
            <div className="cell status-cell">
              <StatusToggle
                id={`gateman-approval-${g.email}`}
                isActive={!!g.appovedByAdmin}
                disabled={savingEmail === g.email}
                onToggle={(next) => handleStatusToggle(g.email, next)}
                activeLabel="APPROVED"
                inactiveLabel="PENDING"
              />
            </div>
          </div>
        ))}

        {!isTableLoading && (!filtered || filtered.length === 0) && (
          <div className="table-row">
            <div className="cell no-results-cell">No gatemen approvals found.</div>
          </div>
        )}
      </div>

      {error && (
        <div className="error-message" style={{ marginTop: 12 }}>{error}</div>
      )}

      {showDeactivateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Un-approve Gateman</h3>
              <button 
                className="modal-close"
                onClick={handleCloseDeactivateModal}
              >
                ×
              </button>
            </div>
            <div className="deactivate-form">
              <div className="warning-message">
                <strong>Warning:</strong> You are about to un-approve
                {` ${deactivateData.name || deactivateData.email}`}. This action will mark the request as pending.
              </div>
              <div className="form-group">
                <label htmlFor="gatemanUnapproveReason">
                  Reason for un-approval <span style={{ color: 'red' }}>*</span>
                </label>
                <textarea
                  id="gatemanUnapproveReason"
                  name="reason"
                  value={deactivateData.reason}
                  onChange={(e) => setDeactivateData(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Please provide a reason for un-approving this request..."
                  rows="4"
                  className="deactivate-reason-textarea"
                  required
                />
              </div>
              <div className="form-actions">
                <button 
                  type="button" 
                  className="admin-btn admin-btn-secondary"
                  onClick={handleCloseDeactivateModal}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="admin-btn admin-btn-danger"
                  onClick={handleConfirmDeactivation}
                  disabled={savingEmail === deactivateData.email}
                >
                  {savingEmail === deactivateData.email ? 'Updating...' : 'Un-approve'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GatemenApproval;


